import { useEffect, useRef, useState } from 'react';
import { ArrowRight, ClipboardList } from 'lucide-react';
import { ActionStack, Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { PanelSkeleton, StateView } from '@/components/ui/Feedback';
import { Helper, Kicker, Lead, Meta, PageTitle, ProgressBar } from '@/components/ui/Surface';
import { useBackendSurveyStore } from '@/store/backendSurveyStore';
import { useUiStore } from '@/store/uiStore';
import {
  QuestionField,
  draftOf,
  isAnswered,
  isRequired,
  isSkippedAnswer,
  type QuestionDraft,
} from './QuestionField';
import { questionParts } from '@/lib/titles';

/**
 * Анкета регистрации по схеме от бэкенда.
 *
 * Вопросы идут по одному: их около двадцати пяти, и длинная простыня на
 * телефоне не читается. Ответ сохраняется при переходе к следующему вопросу,
 * поэтому анкету можно прервать и вернуться — сервер помнит заполненное.
 *
 * Состояния: загрузка, ошибка с повтором, пустая схема, вопрос, завершение.
 */
export function RegistrationSurveyScreen() {
  const questions = useBackendSurveyStore((state) => state.questions);
  const answers = useBackendSurveyStore((state) => state.answers);
  const loading = useBackendSurveyStore((state) => state.loading);
  const error = useBackendSurveyStore((state) => state.error);
  const completing = useBackendSurveyStore((state) => state.completing);
  const reload = useBackendSurveyStore((state) => state.reload);
  const saveAnswer = useBackendSurveyStore((state) => state.saveAnswer);
  const complete = useBackendSurveyStore((state) => state.complete);
  const toast = useUiStore((state) => state.toast);

  const [index, setIndex] = useState(0);
  const [draft, setDraft] = useState<QuestionDraft>({ value: null, otherValue: '', comment: '' });
  /** Место продолжения ищется один раз: дальше человек ходит по анкете сам. */
  const resumed = useRef(false);

  useEffect(() => {
    if (questions.length === 0) void reload();
  }, [questions.length, reload]);

  // Анкету заполняют в несколько заходов. Начинать каждый раз с первого
  // вопроса — значит проходить два десятка нажатий «Далее» до места, где
  // человек остановился
  useEffect(() => {
    if (resumed.current || questions.length === 0) return;
    resumed.current = true;

    const next = questions.findIndex((item) => !isAnswered(item, draftOf(answers[item.key])));
    if (next > 0) setIndex(next);
  }, [questions, answers]);

  const question = questions[index];

  // Черновик берётся из сохранённых ответов при каждой смене вопроса
  useEffect(() => {
    if (question) setDraft(draftOf(answers[question.key]));
  }, [question, answers]);

  if (loading && questions.length === 0) {
    return (
      <Screen subtitle="Анкета" onboarding>
        <PanelSkeleton count={3} />
      </Screen>
    );
  }

  if (error && questions.length === 0) {
    return (
      <Screen subtitle="Анкета" onboarding>
        <StateView
          icon={<ClipboardList className="h-6 w-6" aria-hidden />}
          title="Анкета не загрузилась"
          description={error}
          primary={
            <Button variant="primary" block onClick={() => void reload()}>
              Попробовать снова
            </Button>
          }
        />
      </Screen>
    );
  }

  if (!question) {
    return (
      <Screen subtitle="Анкета" onboarding>
        <StateView
          icon={<ClipboardList className="h-6 w-6" aria-hidden />}
          title="Вопросов пока нет"
          description="Анкета для этого профиля ещё не настроена. Попробуйте обновить или напишите в поддержку."
          primary={
            <Button variant="primary" block onClick={() => void reload()}>
              Обновить
            </Button>
          }
        />
      </Screen>
    );
  }

  const last = index === questions.length - 1;
  const ready = isAnswered(question, draft);
  // Часть вопросов приходит в две строки: формулировка и пояснение под ней
  const parts = questionParts(question);

  // Анкету можно завершить досрочно, когда всё обязательное уже отвечено:
  // иначе возвращение к заполненной анкете стоит два десятка нажатий
  const remaining = questions.filter(
    (item) => isRequired(item) && !isAnswered(item, draftOf(answers[item.key]))
  ).length;
  const canFinishEarly = !last && remaining === 0 && ready;

  const saveCurrent = async () => {
    // Необязательный вопрос без ответа не отправляется: бэкенд пустое значение
    // не принимает, и пропуск выглядел как сбой сохранения
    if (isSkippedAnswer(question, draft)) return true;

    const saved = await saveAnswer({
      key: question.key,
      value: draft.value,
      otherValue: draft.otherValue || undefined,
      comment: draft.comment || undefined,
    });
    // Молча идти дальше нельзя: ответ остался бы только на экране, а при
    // следующем открытии анкеты исчез бы
    if (!saved) toast('Ответ не сохранён. Проверьте связь и попробуйте ещё раз', 'error');
    return saved;
  };

  const goNext = async () => {
    if (!(await saveCurrent())) return;

    if (!last) {
      setIndex(index + 1);
      return;
    }

    await finish();
  };

  // Возврат тоже сохраняет: набранный ответ пропадал при взгляде на прошлый вопрос
  const goBack = async () => {
    await saveCurrent();
    setIndex(index - 1);
  };

  const finish = async () => {
    if (await complete()) return;
    toast(useBackendSurveyStore.getState().error ?? 'Не удалось завершить анкету', 'error');
  };

  return (
    <Screen subtitle="Анкета" onboarding>
      <Kicker>Профессиональный контекст</Kicker>
      {/*
        Полоса вместо отдельных отметок: вопросов больше двух десятков, и
        двадцать шесть чёрточек на телефоне читаются хуже одной шкалы
      */}
      <ProgressBar value={Math.round(((index + 1) / questions.length) * 100)} />
      <Meta>
        Вопрос {index + 1} из {questions.length}
      </Meta>

      <PageTitle>{parts.title}</PageTitle>
      {parts.description ? <Lead>{parts.description}</Lead> : null}

      <QuestionField question={question} draft={draft} onChange={setDraft} />

      {!isRequired(question) ? <Helper>Этот вопрос можно пропустить</Helper> : null}

      <ActionStack>
        <Button
          variant="primary"
          block
          loading={completing}
          disabled={!ready}
          onClick={() => void goNext()}
          iconRight={<ArrowRight className="h-4 w-4" aria-hidden />}
        >
          {last ? 'Завершить анкету' : 'Далее'}
        </Button>
        {canFinishEarly ? (
          <Button
            block
            loading={completing}
            onClick={async () => {
              if (await saveCurrent()) await finish();
            }}
          >
            Завершить анкету
          </Button>
        ) : null}
        {index > 0 ? (
          // Возврат внутри анкеты, а не выход из неё: системная кнопка «Назад»
          // увела бы на предыдущий шаг регистрации и потеряла бы прогресс
          <Button variant="ghost" block onClick={() => void goBack()}>
            Назад
          </Button>
        ) : null}
      </ActionStack>
    </Screen>
  );
}
